import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import type { Post } from '../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, FileEdit, Edit, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAppDispatch } from '@/store/hooks';
import { deletePost } from '../store/blogSlice';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PostsListProps {
  posts: Post[];
  blogId: number;
  isAdmin: boolean;
  selectedCategoryName?: string;
}

export const PostsList = ({
  posts,
  isAdmin,
  selectedCategoryName,
}: PostsListProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<Post | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, post: Post) => {
    e.stopPropagation(); // Prevent card click
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (e: React.MouseEvent, post: Post) => {
    e.stopPropagation(); // Prevent card click
    navigate(`/posts/${post.slug}/edit`);
  };

  const handleDelete = async () => {
    if (!postToDelete) return;

    setIsDeleting(true);
    try {
      await dispatch(deletePost(postToDelete.id)).unwrap();
      toast.success('Đã xóa bài viết thành công');
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    } catch (error: any) {
      toast.error(error?.message || 'Không thể xóa bài viết');
    } finally {
      setIsDeleting(false);
    }
  };

  // All posts are published now, no need to filter
  const visiblePosts = posts;

  if (visiblePosts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <FileEdit className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {selectedCategoryName
            ? `Chưa có bài viết trong "${selectedCategoryName}"`
            : 'Chưa có bài viết nào'}
        </h3>
        <p className="text-gray-600">
          {isAdmin
            ? 'Bắt đầu bằng cách tạo bài viết đầu tiên'
            : 'Chưa có nội dung được xuất bản'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {selectedCategoryName && (
        <h2 className="text-2xl font-bold mb-4">
          Danh mục: {selectedCategoryName}
        </h2>
      )}

      <div className="grid gap-4">
        {visiblePosts.map((post) => (
          <Card
            key={post.id}
            className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
            onClick={() => navigate(`/posts/${post.slug}`)}
          >
            <div className={post.cover_image_url ? 'flex flex-col gap-5 p-4 sm:flex-row sm:gap-4' : undefined}>
              {post.cover_image_url && (
              <div className="w-full sm:w-1/3 sm:max-w-[240px] flex items-center justify-center shrink-0">
                <img
                  src={post.cover_image_url}
                  alt={post.title}
                  loading="lazy"
                  className="block w-full h-auto max-h-[28rem] object-contain"
                />
              </div>
              )}
              <div className="flex-1 min-w-0">
                <CardHeader className={post.cover_image_url ? 'p-0' : undefined}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="line-clamp-2 mb-2">{post.title}</CardTitle>
                      <CardDescription className="flex flex-wrap items-center gap-3 text-xs">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {post.published_at
                            ? formatDate(post.published_at)
                            : formatDate(post.created_at)}
                        </span>
                        {post.category && (
                          <Badge variant="outline" className="text-xs">
                            {post.category.name}
                          </Badge>
                        )}
                      </CardDescription>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleEditClick(e, post)}
                          className="h-8 px-2"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDeleteClick(e, post)}
                          className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className={post.cover_image_url ? 'p-0 pt-4' : undefined}>
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {post.content.replace(/<[^>]*>/g, '').substring(0, 200)}
                    {post.content.length > 200 ? '...' : ''}
                  </p>
                  <p className="text-sm font-semibold text-blue-600 mt-3">
                    Xem thêm
                  </p>
                </CardContent>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          // Keep the confirmation modal open while the Drive/database deletion
          // is in progress so the page cannot appear to change silently.
          if (!isDeleting) setDeleteDialogOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa bài viết</AlertDialogTitle>
            <AlertDialogDescription>
              {isDeleting
                ? 'Đang xóa bài viết và các tệp phương tiện liên quan...'
                : `Bạn có chắc chắn muốn xóa bài viết "${postToDelete?.title}" không? Hành động này không thể hoàn tác.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Đang xóa...
                </>
              ) : 'Xóa bài viết'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
